/**
 * The ONE definition of a test fixture RECORD (#6189, DR-109; design: docs/architecture/test-record-marker-design.md).
 * A fixture record (a test ticket, an invoice draft built from one, a void ticket) is real data shaped like a customer's, so
 * billing, payroll and reports need a machine-checkable flag to skip it. `testAccount.ts` answers for LOGINS; this answers for
 * DOCUMENTS.
 *
 * `isTestRecord === true` is the marker. Only the fixture builder script writes it (and `testRunId`); no callable takes either
 * from a payload. A document with no marker is real. The marker cannot live in QuickBooks, so a document whose QuickBooks
 * customer is a known test customer (the "ZZ OFFICEHUB TEST" ids, one per realm) is a test record too; those ids are
 * configuration passed in by the caller, never literals here. Unknown shapes are NOT test records: hiding a real customer's
 * record from billing is the worse error.
 *
 * Not yet copied into functions-shared: no functions-ai reader needs it. When one does, copy it with a parity test (testAccountRule.ts pattern).
 */
export interface TestRecordShape {
  isTestRecord?: unknown;
  realmId?: unknown;
  qboCustomerId?: unknown;
}

/** One entry per known test customer: `${realmId}:${qboCustomerId}`. */
export type TestCustomerKeys = ReadonlySet<string>;

export function testCustomerKey(realmId: string, qboCustomerId: string): string {
  return `${realmId}:${qboCustomerId}`;
}

/** Parse a list of `realmId:customerId` entries. Each segment is trimmed. Malformed entries are returned in `rejected`, never silently dropped. */
export function parseTestCustomerKeys(entries: ReadonlyArray<unknown> | null | undefined): { keys: TestCustomerKeys; rejected: string[] } {
  const keys = new Set<string>();
  const rejected: string[] = [];
  for (const entry of entries ?? []) {
    const [realm, id, ...rest] = String(entry).split(":").map((x) => x.trim());
    if (realm && id && rest.length === 0) keys.add(testCustomerKey(realm, id));
    else rejected.push(String(entry));
  }
  return { keys, rejected };
}

export function isTestRecord(doc: TestRecordShape | null | undefined, testCustomers: TestCustomerKeys = new Set<string>()): boolean {
  if (!doc) return false;
  if (doc.isTestRecord === true) return true;
  const { realmId, qboCustomerId } = doc;
  // QuickBooks ids are often numeric; a numeric id must match the configured string (the dangerous direction is NOT refusing).
  const scalar = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : typeof v === "number" && Number.isFinite(v) ? String(v) : null);
  const realm = scalar(realmId);
  const customer = scalar(qboCustomerId);
  return realm !== null && customer !== null && testCustomers.has(testCustomerKey(realm, customer));
}
